import type { UpdateDappFormData } from '../../../../lib/schemas/formSchemas';
import { ensureHttpsUrl } from '../../../../utils/nftMetadataHelpers';
import type { FormState, UpdateDappInput } from '../types';

interface BuildUpdateDappPayloadParams {
  dappId: string;
  initialData: FormState | null;
  validatedData: UpdateDappFormData;
}

export function buildUpdateDappPayload({
  dappId,
  initialData,
  validatedData,
}: BuildUpdateDappPayloadParams): UpdateDappInput {
  const updateData: UpdateDappInput = {
    dappId,
  };

  if (!initialData) {
    return updateData;
  }

  if (validatedData.dappName !== initialData.dappName) {
    updateData.dappName = validatedData.dappName;
  }

  if (validatedData.subtitle !== initialData.subtitle) {
    updateData.subtitle = validatedData.subtitle || undefined;
  }

  if (validatedData.description !== initialData.description) {
    updateData.description = validatedData.description;
  }

  if (
    JSON.stringify(validatedData.languages) !==
    JSON.stringify(initialData.languages)
  ) {
    updateData.languages = validatedData.languages;
  }

  if (
    validatedData.availableCountriesMode !==
      initialData.availableCountriesMode ||
    JSON.stringify(validatedData.availableCountries) !==
      JSON.stringify(initialData.availableCountries)
  ) {
    updateData.availableCountriesMode = validatedData.availableCountriesMode;
    updateData.availableCountries = validatedData.availableCountries;
  }

  if (validatedData.appWebsite !== initialData.appWebsite) {
    updateData.appWebsite = ensureHttpsUrl(validatedData.appWebsite || '');
  }

  if (validatedData.contactEmail !== initialData.contactEmail) {
    updateData.contactEmail = validatedData.contactEmail;
  }

  if (validatedData.supportEmail !== initialData.supportEmail) {
    updateData.supportEmail = validatedData.supportEmail;
  }

  if (validatedData.tokenDistribution !== initialData.tokenDistribution) {
    updateData.tokenDistribution = validatedData.tokenDistribution;
  }

  if (validatedData.walletAddress !== initialData.walletAddress) {
    updateData.walletAddress = validatedData.walletAddress || undefined;
  }

  if (validatedData.licenseUrl !== initialData.licenseUrl) {
    updateData.licenseUrl = ensureHttpsUrl(validatedData.licenseUrl || '');
  }

  if (validatedData.privacyPolicyUrl !== initialData.privacyPolicyUrl) {
    updateData.privacyPolicyUrl = ensureHttpsUrl(
      validatedData.privacyPolicyUrl || '',
    );
  }

  // Editor's Choice inputs are absent from the form until the publisher opts in.
  if (
    (validatedData.editorsChoiceHeadline ?? '') !==
    initialData.editorsChoiceHeadline
  ) {
    updateData.editorsChoiceHeadline =
      validatedData.editorsChoiceHeadline || null;
  }

  if ((validatedData.editorsChoiceTag ?? '') !== initialData.editorsChoiceTag) {
    updateData.editorsChoiceTag = validatedData.editorsChoiceTag || null;
  }

  if (validatedData.releaseApprovalMode !== initialData.releaseApprovalMode) {
    updateData.releaseApprovalMode = validatedData.releaseApprovalMode;
  }

  return updateData;
}

export function resolvePresignedUrl(
  url: string | null,
  presignedUrls: Record<string, string>,
): string | null {
  if (!url) {
    return null;
  }

  return presignedUrls[url] || url;
}

export function resolvePresignedUrls(
  urls: (string | null)[],
  presignedUrls: Record<string, string>,
): (string | null)[] {
  return urls.map((url) => resolvePresignedUrl(url, presignedUrls));
}
